/**
 * Front doors (17 §4).
 *
 * The ad that brought somebody in is the first thing they see. Sixty ads on the
 * board make sixty different promises and, until now, all of them landed on the
 * same eight seconds of desk. A door is the fix: the ad's own picture, the ad's
 * own line, and then the same workshop everyone else gets.
 *
 * The poster is what the eye lands on first, and index.html draws it before
 * the app has loaded. The doors were built on the assumption that people tap
 * past them (2026-09-18); in fact they do watch the clip, but take a moment to
 * see that the door wants a tap (owner, 2026-10-08). The promise is still
 * carried onto the next screen for whoever does skip. A clip is a bonus a door
 * may not have; only `desk` has one today and the rest are stills until
 * somebody asks for more. index.html carries its own copy of these doors
 * (door-preload.test.ts holds it to this one).
 *
 * What a door may change: the poster, the clip, the quote, the promise. What a
 * door may never change: anything after the first card. The crew, the engine,
 * the card, the feed and the road are one product for everybody.
 */
export interface DoorDef {
  /** url slug, `?d=term`, and the `entry` dimension on every event */
  slug: string;
  /** who this door is for — for the write-up and for reading a funnel later */
  audience: string;
  /** the still. Always present: this is the door for anyone who skips. */
  art: string;
  /**
   * The clip, when there is one. Absent means the poster holds the screen and
   * there is no play affordance at all — a "Tap to play" over a still that
   * cannot play is worse than no clip.
   */
  clip?: string;
  /**
   * The screen behind the poster, and the colour the words on it are set in.
   * Sampled from the top of each poster, because the quote sits over it: dark
   * ink on the terminal door would be invisible, and the scrim that keeps the
   * words readable is made out of `ground` so it never fights the picture.
   * It is also what shows while the image is still loading, or if it never
   * arrives, so the door is the right colour before it is the right picture.
   */
  ground: string;
  ink: string;
  /**
   * Which part of the picture to keep when the screen is a different shape from
   * it (CSS object-position). The default centres, which is right for a picture
   * of a room and wrong for left-aligned terminal text, where centring eats the
   * `$` and the first letters of every line.
   */
  focus?: string;
  /**
   * How solid the band of `ground` behind the words is, 0 to 1. The default
   * suits a picture with a calm edge. The psych door needs nearly all of it:
   * its art is loud in every direction, and a half-transparent scrim over
   * ornament leaves the quote sitting in a hedge.
   */
  scrim?: number;
  /**
   * The quote laid over the picture, chosen for THAT picture — the whole point
   * of a door is that it continues the ad, and one quote on four pictures is
   * exactly what this was built to stop. It must be one of the verified quotes
   * in `quotes.json`; the test enforces that, because the first screen of the
   * app is the worst place to have a misattributed line.
   *
   * `null` means no words at all. A picture that is itself made of words — the
   * terminal door — gets none: a serif quote over somebody else's typography is
   * unreadable at best, and once it is legible it is still two voices talking
   * at once. Those doors say what they have to say in the picture.
   */
  quote?: { text: string; by: string } | null;
  /**
   * The ad's own line, in the ad's own words. Shown under the quote and again
   * on the question screen, which is the only part of the door a skipper sees.
   */
  promise: string;
  /** the plain-language look, so a door can be talked about without opening it */
  look: string;
  /**
   * A door that is deliberately another door's twin: the same poster, quote
   * and promise, under its own slug, so a new ad can be told apart in the
   * funnel (`entry`) without changing what its visitors see. The only doors
   * allowed to share a quote.
   */
  copyOf?: string;
}

export const DEFAULT_DOOR = 'desk';

export const DOORS: Record<string, DoorDef> = {
  desk: {
    slug: 'desk',
    audience: 'anyone — organic, the quote ads, the 30-day promise',
    art: '/art/doors/desk.webp',
    ground: '#F0E9DE',
    ink: '#3B3A37',
    clip: '/art/door.mp4',
    // the blank card in the lamplight: you were an amateur once, and it was allowed
    quote: { text: 'Every artist was first an amateur.', by: 'Ralph Waldo Emerson' },
    promise: 'Thirty days to become creative again.',
    look: 'a grown-up desk at night, one blank index card in the lamplight',
  },
  term: {
    slug: 'term',
    audience: 'engineers — the leetcode and leetcreate ads',
    art: '/art/doors/term.webp',
    ground: '#000000',
    ink: '#FFFFFF',
    focus: 'left center',
    // the picture is already a screen full of type; nothing goes on top of it
    quote: null,
    promise: 'You solved three hundred problems. None of them were this one.',
    look: 'a terminal holding your whole day, with an empty prompt underneath it',
  },
  stopped: {
    slug: 'stopped',
    audience: 'the professional who used to make things — the domain ads',
    art: '/art/doors/stopped.webp',
    ground: '#4B482B',
    ink: '#F0E9DE',
    // an orderly, sane, airless room, and the line that names what is missing from it
    quote: { text: 'Art is a guaranty of sanity.', by: 'Louise Bourgeois' },
    promise: 'You already finished the hard degree. Now be creative again.',
    look: 'an airless room, a desk too long for it, one white card',
  },
  blank: {
    slug: 'blank',
    audience: 'the blocked — “afraid of a blank page?”',
    art: '/art/doors/blank.webp',
    ground: '#3A2740',
    ink: '#F7ECD8',
    scrim: 0.96,
    // a house of pattern in every direction, with one blank card in the middle of it
    quote: { text: 'I dwell in Possibility — / A fairer House than Prose —', by: 'Emily Dickinson' },
    promise: 'Afraid of a blank page? The card is already dealt.',
    look: 'ornament in every direction, and one plain white card in the middle',
  },

  // Ad batch 2 (AUX frame K48, 2026-10-13): style (plush / kondo) × message
  // (again / novel / work). Each poster is its ad with the words taken off, so
  // the style under test is the style they land in, and the empty wall where
  // the headline was is where the quote goes.
  'again-plush': {
    slug: 'again-plush',
    audience: 'batch 2 — hope, general, in the plush workshop',
    art: '/art/doors/again-plush.webp',
    ground: '#FBF4DC',
    ink: '#1B4FA8',
    // Kraben finds the sketchbook he forgot he loved: play, taken seriously
    quote: { text: 'Serious art is born from serious play.', by: 'Julia Cameron' },
    promise: '21 days to become creative again.',
    look: 'the crew round an old sketchbook from the bench drawer, Kraben blowing the dust off it',
  },
  'again-kondo': {
    slug: 'again-kondo',
    audience: 'batch 2 — hope, general, in Kondo',
    art: '/art/doors/again-kondo.webp',
    ground: '#EDE1D0',
    ink: '#2E2A26',
    // the sketchbook is still there, and nothing in it was used up
    quote: { text: "You can't use up creativity. The more you use, the more you have.", by: 'Maya Angelou' },
    promise: '21 days to become creative again.',
    look: 'oatmeal paper, a clay claw lifting the cover of an old sketchbook',
  },
  // Ad H2 (2026-10-15): the again-plush ad, plus the imagine starter's own
  // example (a photo + a place = a drawing of you there). Same door as
  // again-plush; its own slug so the two ads' funnels can be compared.
  'again-photo': {
    slug: 'again-photo',
    copyOf: 'again-plush',
    audience: 'H2 ad — plush workshop style plus the photo + place = drawing example, to test whether seeing the photo step in the ad makes people do it',
    art: '/art/doors/again-plush.webp',
    ground: '#FBF4DC',
    ink: '#1B4FA8',
    quote: { text: 'Serious art is born from serious play.', by: 'Julia Cameron' },
    promise: '21 days to become creative again.',
    look: 'the again-plush door: the crew round an old sketchbook from the bench drawer, Kraben blowing the dust off it',
  },
  'novel-plush': {
    slug: 'novel-plush',
    audience: 'batch 2 — hope, the book, in the plush workshop',
    art: '/art/doors/novel-plush.webp',
    ground: '#FBF4DC',
    ink: '#1B4FA8',
    // nobody holding up Chapter One knew yet what kind of writer they were
    quote: { text: "Don't wait until you know who you are to get started.", by: 'Austin Kleon' },
    promise: '21 days to the first page of your novel.',
    look: 'Kraben holding up one typed page headed CHAPTER ONE, the Moon reading over his shoulder',
  },
  'novel-kondo': {
    slug: 'novel-kondo',
    audience: 'batch 2 — hope, the book, in Kondo',
    art: '/art/doors/novel-kondo.webp',
    ground: '#EDE1D0',
    ink: '#2E2A26',
    // a novelist on where the next page comes from
    quote: { text: 'Inspiration usually comes during work, rather than before it.', by: "Madeleine L'Engle" },
    promise: '21 days to the first page of your novel.',
    look: 'oatmeal paper, one sheet headed Chapter One held up by a clay claw',
  },
  // The ad says "Work is changing with AI." The promise drops the AI: it is on
  // the first screens' banned list (intro-copy), and the door's job is to
  // carry the ad's feeling in, not to say the word a second time.
  'work-plush': {
    slug: 'work-plush',
    audience: 'batch 2 — fear, work changing, in the plush workshop',
    art: '/art/doors/work-plush.webp',
    ground: '#FBF4DC',
    ink: '#1B4FA8',
    // he walks out of the grey office into the workshop to play
    quote: { text: 'Letting your mind play is the best way to solve problems.', by: 'Bill Watterson' },
    promise: 'Work is changing. Get creative again.',
    look: 'a grey office where the laptop types by itself, and Kraben walking out of it into the workshop',
  },
  'work-kondo': {
    slug: 'work-kondo',
    audience: 'batch 2 — fear, work changing, in Kondo',
    art: '/art/doors/work-kondo.webp',
    ground: '#EDE1D0',
    ink: '#2E2A26',
    // a grid of identical laptops and one open notebook: making as a working habit
    quote: { text: 'Creativity is a habit, and the best creativity is the result of good work habits.', by: 'Twyla Tharp' },
    promise: 'Work is changing. Get creative again.',
    look: 'oatmeal paper, a grid of identical closed laptops with one open notebook in it',
  },
};

/** The door for a slug, or the default. Never throws, never returns undefined. */
export function getDoor(slug?: string | null): DoorDef {
  return isDoor(slug) ? DOORS[slug as string] : DOORS[DEFAULT_DOOR];
}

/**
 * Own properties only. A plain `DOORS[slug]` answers `?d=constructor` with
 * `Object.prototype.constructor` — a truthy function that is not a door and
 * has no `slug`, which reaches the screen as a blank one.
 */
export function isDoor(slug?: string | null): boolean {
  return !!slug && Object.prototype.hasOwnProperty.call(DOORS, slug);
}
